import React from 'react';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function DayView({
  year,
  month,
  selectedDate,
  onSelectDate,
  onMonthChange,
  onYearChange,
  events,
  onEventClick,
  onDeleteEventClick,
  onAddNewClick
}) {
  // Days in current month logic
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = (new Date(year, month, 1).getDay() + 6) % 7; // Monday start index 0..6

  // Previous month fill days
  const prevMonthDays = new Date(year, month, 0).getDate();

  // Create grid cells array
  const gridCells = [];
  
  // Empty / Previous month cells
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    gridCells.push({
      day: prevMonthDays - i,
      isCurrentMonth: false,
      dateStr: null
    });
  }

  // Current month cells
  for (let day = 1; day <= daysInMonth; day++) {
    const formattedMonth = String(month + 1).padStart(2, '0');
    const formattedDay = String(day).padStart(2, '0');
    const dateStr = `${year}-${formattedMonth}-${formattedDay}`;
    gridCells.push({
      day,
      isCurrentMonth: true,
      dateStr
    });
  }

  // Next month fill cells to make 35 or 42 cells
  const totalGrid = Math.ceil(gridCells.length / 7) * 7;
  const remaining = totalGrid - gridCells.length;
  for (let i = 1; i <= remaining; i++) {
    gridCells.push({
      day: i,
      isCurrentMonth: false,
      dateStr: null
    });
  }

  // Helper to get events for a date string YYYY-MM-DD (matches single date or start_date..end_date range)
  const getEventsForDate = (dateStr) => {
    if (!dateStr || !events) return [];
    const target = dateStr.slice(0, 10);
    return events.filter(e => {
      const sDate = (e.start_date || e.event_date || '').slice(0, 10);
      const eDate = (e.end_date || sDate || '').slice(0, 10);
      if (sDate && eDate) {
        return target >= sDate && target <= eDate;
      }
      return (e.event_date || '').slice(0, 10) === target || sDate === target;
    });
  };

  // Format date info for Scratch Design Panel
  const selectedDateObj = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date();
  const dayName = selectedDateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const dayNumber = String(selectedDateObj.getDate()).padStart(2, '0');
  const monthName = selectedDateObj.toLocaleDateString('en-US', { month: 'long' });
  const selectedEvents = getEventsForDate(selectedDate);

  return (
    <div className="day-view-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '16px' }}>
      
      {/* Event Categories Legend Bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', backgroundColor: '#ffffff', padding: '12px 24px', borderRadius: '12px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event Types:</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#0b66e3' }} />
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Calendar Event</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#8b5cf6' }} />
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Situation Stage</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10b981' }} />
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>Note</span>
        </div>
      </div>

      {/* 1. Date Header & Active Day Details */}
      <div
        className="date-details-panel"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid var(--border-color, #e2e8f0)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
            Calendar &gt; <span style={{ fontWeight: '600', color: '#0f172a' }}>{monthName} {parseInt(dayNumber, 10)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '32px', marginTop: '4px' }}>
          {/* Day Name & Large Date Badge */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '96px', alignItems: 'center' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.01em', width: '100%', textAlign: 'left' }}>
              {dayName}
            </h2>
            <div
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: '36px',
                fontWeight: '700',
                color: '#0f172a',
                lineHeight: '1'
              }}
            >
              {dayNumber}
            </div>
          </div>

          {/* Events List for Selected Date */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>
              Events On {selectedDate === new Date().toISOString().split('T')[0] ? 'today' : `${monthName} ${parseInt(dayNumber, 10)}`}
            </div>

            {selectedEvents.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {selectedEvents.map((evt) => {
                  const dotColor = evt.color || '#0b66e3';
                  const badgeBg = evt.bg_color || '#eff6ff';
                  const textColor = evt.text_color || '#0b66e3';

                  return (
                    <div
                      key={evt.id}
                      onClick={() => onEventClick(evt)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 18px',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        borderLeft: `4px solid ${dotColor}`,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                      }}
                      className="event-list-item"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
                        <span style={{ fontSize: '11px', fontWeight: '700', color: textColor, backgroundColor: badgeBg, padding: '2px 8px', borderRadius: '6px', flexShrink: 0 }}>
                          {evt.category || 'Event'}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', flexShrink: 0 }}>
                          {evt.event_time || '09:00'}
                        </span>
                        <span
                          style={{
                            fontSize: '14px',
                            color: '#334155',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {evt.event_name}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0, marginLeft: '16px' }}>
                        {evt.type === 'event' && (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEventClick(evt);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#0b66e3',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteEventClick(evt);
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
                No events scheduled for this date.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. Monthly Calendar Grid */}
      <div
        className="calendar-grid-card"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid var(--border-color, #e2e8f0)',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        {/* Month & Year Navigation Switcher */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            {MONTH_NAMES[month]} {year}
          </h3>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="action-btn-outlined"
              onClick={() => onYearChange && onYearChange(-1)}
              style={{ padding: '6px 12px', height: '36px' }}
              title="Previous Year"
            >
              &lt;&lt; {year - 1}
            </button>
            <button
              type="button"
              className="action-btn-outlined"
              onClick={() => onMonthChange(-1)}
              style={{ padding: '6px 12px', height: '36px' }}
            >
              &lt; Prev Month
            </button>
            <button
              type="button"
              className="action-btn-outlined"
              onClick={() => onMonthChange(1)}
              style={{ padding: '6px 12px', height: '36px' }}
            >
              Next Month &gt;
            </button>
            <button
              type="button"
              className="action-btn-outlined"
              onClick={() => onYearChange && onYearChange(1)}
              style={{ padding: '6px 12px', height: '36px' }}
              title="Next Year"
            >
              {year + 1} &gt;&gt;
            </button>
          </div>
        </div>

        {/* Weekdays Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px', marginBottom: '12px', textAlign: 'center', width: '100%', boxSizing: 'border-box' }}>
          {WEEKDAYS.map((w) => (
            <div key={w} style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 0, overflow: 'hidden' }}>
              {w}
            </div>
          ))}
        </div>

        {/* Calendar Grid Cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px', width: '100%', boxSizing: 'border-box' }}>
          {gridCells.map((cell, idx) => {
            const dateEvents = getEventsForDate(cell.dateStr);
            const isSelected = cell.dateStr && cell.dateStr === selectedDate;
            const hasEvents = dateEvents.length > 0;

            return (
              <div
                key={idx}
                onClick={() => {
                  if (cell.isCurrentMonth && cell.dateStr) {
                    onSelectDate(cell.dateStr);
                  }
                }}
                style={{
                  minHeight: '90px',
                  minWidth: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: '12px',
                  border: isSelected
                    ? '2px solid var(--primary-color, #0b66e3)'
                    : '1px solid #f1f5f9',
                  backgroundColor: isSelected
                    ? 'rgba(11, 102, 227, 0.04)'
                    : cell.isCurrentMonth
                    ? '#ffffff'
                    : '#f8fafc',
                  opacity: cell.isCurrentMonth ? 1 : 0.4,
                  padding: '8px',
                  cursor: cell.isCurrentMonth ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Date Number & Event Indicator Dot */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: isSelected ? '700' : '600',
                      color: isSelected ? 'var(--primary-color, #0b66e3)' : '#1e293b'
                    }}
                  >
                    {cell.day}
                  </span>

                  {hasEvents && (
                    <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexShrink: 0 }}>
                      {dateEvents.slice(0, 3).map((evt, i) => (
                        <span
                          key={i}
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: evt.color || 'var(--primary-color, #0b66e3)'
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Stacked Compact Event Chips */}
                {hasEvents && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '2px', overflow: 'hidden', minWidth: 0, width: '100%' }}>
                    {dateEvents.slice(0, 2).map((evt) => {
                      const chipBg = evt.bg_color || '#eff6ff';
                      const chipText = evt.text_color || '#0b66e3';
                      return (
                        <div
                          key={evt.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(evt);
                          }}
                          style={{
                            backgroundColor: chipBg,
                            color: chipText,
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '10px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minWidth: 0,
                            maxWidth: '100%',
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                          title={evt.event_name}
                        >
                          {evt.event_name}
                        </div>
                      );
                    })}
                    {dateEvents.length > 2 && (
                      <span style={{ fontSize: '10px', color: '#64748b', fontWeight: '600', alignSelf: 'flex-start' }}>
                        +{dateEvents.length - 2} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DayView;
